import React, { useState, useEffect, useMemo } from 'react';
import { styled as styled2 } from '@compiled/react';
import debounce from 'lodash/debounce';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDown from '@atlaskit/icon/core/chevron-down';
import { Text } from '@atlaskit/primitives/compiled';
import TextField from '@atlaskit/textfield';

import { token } from '@atlaskit/tokens';

import { useIntl } from '@atlassian/jira-intl';
import messages from './messages.tsx';

type InputProps = {
	value: number;
	label?: string;
	onChange: (value: number) => void;
};

export const ReferenceDepthInput = ({ value, onChange }: InputProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const referenceDepthOptions = Array.from(Array(5), (_, i) => i + 1);

	return (
		<DropdownMenu
			zIndex={1000}
			trigger={({ triggerRef, ...props }) => (
				<Button
					{...props}
					ref={triggerRef}
					iconAfter={<ChevronDown label="down icon" size="small" />}
					testId="servicedesk-insight-object-graph.ui.graph-config-panel.reference-depth-inputs.trigger-button"
				>
					{value}
				</Button>
			)}
			appearance="tall"
		>
			<DropdownItemGroup>
				{referenceDepthOptions.map((depth) => (
					<DropdownItemWrapper key={depth}>
						<DropdownItem
							onClick={() => onChange(depth)}
							testId="servicedesk-insight-object-graph.ui.graph-config-panel.reference-depth-inputs.depth-option"
						>
							{/* @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'number' can't be used to index type '{ 5: MessageDescriptorV2; 4: MessageDescriptorV2; 3: MessageDescriptorV2; 2: MessageDescriptorV2; 1: MessageDescriptorV2; helpText: MessageDescriptorV2; inboundReferencesLabel: MessageDescriptorV2; referenceDepthLabel: MessageDescriptorV2; }'. */}
							{formatMessage(messages[depth])}
						</DropdownItem>
					</DropdownItemWrapper>
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};

export const InboundReferencesInput = ({
	value,
	onChange,
	label,
}: InputProps): React.JSX.Element => {
	const [internalValue, setInternalValue] = useState(value);
	const debouncedOnChange = useMemo(() => debounce(onChange, 200), [onChange]);

	// eslint-disable-next-line react-you-might-not-need-an-effect/no-reset-all-state-on-prop-change
	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
		setInternalValue(value);
	}, [value]);

	return (
		<TextField
			testId="servicedesk-insight-object-graph.ui.graph-config-panel.reference-depth-inputs.max-of-same-type"
			type="number"
			autoComplete="off"
			min={1}
			max={50}
			value={internalValue}
			aria-label={label}
			onChange={(event) => {
				// @ts-expect-error - TS2339 - Property 'value' does not exist on type 'EventTarget'.
				const { value: updated } = event.target;
				const newValue = Number(updated);
				setInternalValue(newValue);
				debouncedOnChange(newValue);
			}}
		/>
	);
};

type Props = {
	maxOfSameType: number;
	referenceDepth: number;
	onUpdateReferenceDepth: (value: number) => void;
	onUpdateMaxOfSameType: (value: number) => void;
};

export const ReferenceDepthConfigurationInputs = (props: Props): React.JSX.Element => {
	const { maxOfSameType, referenceDepth, onUpdateReferenceDepth, onUpdateMaxOfSameType } = props;
	const { formatMessage } = useIntl();
	return (
		<>
			<InputContainer>
				{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
				<LabelText>{formatMessage(messages.referenceDepthLabel)}:</LabelText>
				<ReferenceDepthInput value={referenceDepth} onChange={onUpdateReferenceDepth} />
			</InputContainer>

			<InputContainer>
				{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
				<LabelText>{formatMessage(messages.inboundReferencesLabel)}:</LabelText>
				<InboundReferencesContainer>
					<InboundReferencesInput
						label={formatMessage(messages.inboundReferencesLabel)}
						value={maxOfSameType}
						onChange={onUpdateMaxOfSameType}
					/>
				</InboundReferencesContainer>
			</InputContainer>

			<Text as="p" size="UNSAFE_small">
				{formatMessage(messages.helpText)}
			</Text>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InputContainer = styled2.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	marginBottom: token('space.100'),
	maxWidth: '200px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownItemWrapper = styled2.div({
	paddingTop: 0,
	paddingRight: token('space.100'),
	paddingBottom: 0,
	paddingLeft: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LabelText = styled2.p({
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InboundReferencesContainer = styled2.div({
	/* this is to ensure the inputs are the same width */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> *': {
		width: '60px',
	},
});
